import React, { useContext, useEffect } from 'react'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import _find from 'lodash/find'
import _findIndex from 'lodash/findIndex'
import _intersection from 'lodash/intersection'

import {
  makeStyles,
  createStyles,
  useScrollTrigger,
  AppBar,
  Toolbar,
  Container,
  ContainerProps,
  Grid,
  IconButton,
  Typography,
  Tabs,
  Tab,
} from '@material-ui/core'
import MenuIcon from '@material-ui/icons/Menu'

import { navItems } from '../../constants/routes'
import useRouter from '../../hooks/useRouter'
import { AppContext, hubModes } from '../../contexts/AppContext'

const useStyles = makeStyles(theme =>
  createStyles({
    appBar: { color: theme.palette.text.secondary },
    container: {
      padding: theme.spacing(0, 2),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(0, 3) },
      [theme.breakpoints.up('md')]: { padding: theme.spacing(0, 4) },
    },

    grid: { ...theme.mixins.toolbar },

    menuButton: { marginRight: theme.spacing(2) },

    title: {
      [theme.breakpoints.up('md')]: { lineHeight: '64px' },
      lineHeight: '56px',
    },
    titleWithTabs: {
      [theme.breakpoints.up('md')]: { marginRight: theme.spacing(3) },
    },

    tabsContainer: {
      alignSelf: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        margin: theme.spacing(0, -3),
        maxWidth: `calc(100% + ${theme.spacing(3) * 2}px)`,
      },
      [theme.breakpoints.down('xs')]: {
        margin: theme.spacing(0, -2),
        maxWidth: `calc(100% + ${theme.spacing(2) * 2}px)`,
      },
    },
    tabsFlexContainer: {
      [theme.breakpoints.down('sm')]: { paddingLeft: theme.spacing(3) },
      [theme.breakpoints.down('xs')]: { paddingLeft: theme.spacing(2) },
    },

    tab: { minWidth: 120 },
  })
)

function a11yProps(index: any) {
  return {
    id: `sub-navigation-tab-${index}`,
    'aria-controls': `sub-navigation-tabpanel-${index}`,
  }
}

interface ITopBarProps {
  isMobile: boolean
  restrictions: string[]
  onDrawerToggle: () => void
  maxWidth?: ContainerProps['maxWidth']
  hubMode: hubModes | undefined
}

const TopBar: React.FunctionComponent<ITopBarProps> = ({
  isMobile,
  restrictions,
  onDrawerToggle,
  maxWidth,
  hubMode,
}) => {
  const classes = useStyles()
  const router = useRouter()
  const appContext = useContext(AppContext)

  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  })

  const _navItems = navItems[hubMode ?? 'PHASE_ONE']
  const topLevelPath = '/' + router.location.pathname?.split('/')[1]
  const topLevelItem =
    _find(
      _navItems,
      item => item.route.toLowerCase() === topLevelPath.toLowerCase()
    ) ??
    _find(
      _navItems,
      item =>
        item.route.toLowerCase() === router.location.pathname.toLowerCase()
    )

  useEffect(() => {
    if (!topLevelItem?.label || !topLevelItem) document.title = 'Antler Hub'
    else if (!document.title.includes(topLevelItem!.label))
      document.title = `${topLevelItem!.label} | Antler Hub`
  }, [topLevelItem?.label])

  return (
    <AppBar
      position="sticky"
      className={classes.appBar}
      color="default"
      elevation={scrollTrigger ? 4 : 0}
    >
      <Toolbar disableGutters>
        <Container maxWidth={maxWidth} className={classes.container}>
          <Grid container className={classes.grid} alignItems="center">
            {isMobile && (
              <Grid item>
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={onDrawerToggle}
                  className={classes.menuButton}
                >
                  <MenuIcon />
                </IconButton>
              </Grid>
            )}

            <Grid item xs>
              <Typography
                variant={isMobile ? 'h5' : 'h4'}
                noWrap
                className={clsx(
                  classes.title,
                  topLevelItem?.children && classes.titleWithTabs
                )}
              >
                {topLevelItem?.label}
                {topLevelItem?.showLocationInLabel &&
                  appContext?.cohortDoc?.location &&
                  ': ' + appContext.cohortDoc.location}
              </Typography>
            </Grid>

            {topLevelItem?.children && (
              <Grid item xs={12} md="auto" className={classes.tabsContainer}>
                <Tabs
                  value={_findIndex(
                    topLevelItem.children,
                    child =>
                      child.route.toLowerCase() ===
                      router.location.pathname.toLowerCase()
                  )}
                  aria-label="subnavigation"
                  variant="scrollable"
                  scrollButtons="auto"
                  classes={{ flexContainer: classes.tabsFlexContainer }}
                  action={actions =>
                    setTimeout(() => actions?.updateIndicator(), 100)
                  }
                >
                  {topLevelItem.children.map((child, i) => {
                    if (
                      restrictions?.includes('development') &&
                      child.restrictions?.includes('development')
                    )
                      return null

                    return (
                      <Tab
                        key={child.route}
                        label={child.label}
                        component={Link}
                        to={child.route}
                        disabled={
                          _intersection(child.restrictions, restrictions)
                            .length > 0
                        }
                        className={classes.tab}
                        {...a11yProps(i)}
                      />
                    )
                  })}
                </Tabs>
              </Grid>
            )}
          </Grid>
        </Container>
      </Toolbar>
    </AppBar>
  )
}

export default TopBar
